import { useState } from "react";
import { SectionCard } from "shared/app/SectionCard";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { useFormTyped, type Register } from "shared/hooks";
import { Row } from "views/applications-id-report/components/Row";
import { AmenitiesForm } from "shared/app/AmenitiesForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Unit } from "types/landlord/address";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { AmenitiesSelect } from "shared/app/AmenitiesForm/components/AmenitiesSelect";
import { PetPolicyPmsField } from "shared/app/AmenitiesForm/components/PetPolicyPmsField";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";

const getYesNo = (value: boolean) => (value ? "Yes" : "No");

const dogSizeLabels: Record<NonNullable<Unit["dog_size"]>, string> = {
  small: "Small only",
  large_and_small: "Large & small"
};

const getDogsAllowedLabel = (unit: Unit) => {
  if (!unit.dogs_allowed) return "No";
  if (unit.dog_size) return dogSizeLabels[unit.dog_size];
  return "Yes";
};

const AmenitiesList = ({ amenities }: { amenities: Unit["amenities"] }) => {
  if (!amenities?.length)
    return <p className="font-medium text-content-1000">-</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {amenities.map((amenity) => (
        <Badge key={amenity.id} variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          {amenity.title}
        </Badge>
      ))}
    </div>
  );
};

interface PmsEditAmenitiesFormValues {
  dogs_allowed?: boolean;
  cats_allowed?: boolean;
  amenities: number[];
  pms_overrides: {
    cats_allowed?: boolean;
    dogs_allowed?: boolean;
    amenities?: boolean;
  };
}

const PmsEditAmenitiesForm = ({
  onClose,
  initialValues,
  unit
}: {
  onClose: () => void;
  initialValues: PmsEditAmenitiesFormValues;
  unit: Unit;
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);
  const [petPolicyTouched, setPetPolicyTouched] = useState<{
    cats_allowed: boolean;
    dogs_allowed: boolean;
  }>({ cats_allowed: false, dogs_allowed: false });
  const overrides = values.pms_overrides;

  const { mutate: updateAmenities, isPending } = useMutation({
    mutationFn: () => {
      const petPolicy = {
        ...(petPolicyTouched.cats_allowed && {
          cats_allowed: values.cats_allowed
        }),
        ...(petPolicyTouched.dogs_allowed && {
          dogs_allowed: values.dogs_allowed
        })
      };
      return instance.patch(`/portal/v1/units/${unit.id}`, {
        amenity_ids: values.amenities,
        ...(Object.keys(petPolicy).length > 0 && { pet_policy: petPolicy }),
        pms_overrides: {
          ...unit.pms_overrides,
          ...values.pms_overrides
        }
      });
    },
    onSuccess: () => {
      toast.success("Amenities updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update amenities: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => updateAmenities()
  });

  const handlePetPolicyChange = (
    field: "cats_allowed" | "dogs_allowed",
    value: unknown
  ) => {
    setPetPolicyTouched((prev) => ({ ...prev, [field]: true }));
    register(field).onChange(value);
  };

  const handleOverrideChange = (
    field: "cats_allowed" | "dogs_allowed",
    synced: boolean
  ) => {
    const newOverrides = { ...overrides, [field]: synced };
    register("pms_overrides").onChange(newOverrides);

    if (synced && unit.pms_values) {
      const pmsVal = unit.pms_values[field];
      if (pmsVal != null) {
        setPetPolicyTouched((prev) => ({ ...prev, [field]: true }));
        register(field).onChange(pmsVal);
      }
    }
  };

  return (
    <SectionCard
      title="Amenities"
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    >
      <div className="space-y-8">
        <PetPolicyPmsField
          id="cat-allowed"
          label="Cat allowed"
          radioName="cats_allowed"
          selectProps={register("cats_allowed")}
          synced={overrides?.cats_allowed ?? true}
          pmsValue={unit.pms_values?.cats_allowed}
          onValueChange={(val) => handlePetPolicyChange("cats_allowed", val)}
          onSyncChange={(val) => handleOverrideChange("cats_allowed", val)}
        />

        <PetPolicyPmsField
          id="dog-allowed"
          label="Dog allowed"
          radioName="dogs_allowed"
          selectProps={register("dogs_allowed")}
          synced={overrides?.dogs_allowed ?? true}
          pmsValue={unit.pms_values?.dogs_allowed}
          onValueChange={(val) => handlePetPolicyChange("dogs_allowed", val)}
          onSyncChange={(val) => handleOverrideChange("dogs_allowed", val)}
        />

        <div className="grid grid-cols-2 gap-4 items-start">
          <div>
            <AmenitiesSelect
              level="unit"
              onSelect={(value) => register("amenities").onChange(value)}
              selectedAmenities={values.amenities}
              disabled={overrides?.amenities ?? true}
            />
            <PmsSyncOption
              value={overrides?.amenities ?? true}
              onChange={(val) => {
                const newOverrides = { ...overrides, amenities: val };
                register("pms_overrides").onChange(newOverrides);
                // When switching ON, mirror the PMS list — including the
                // empty case — so the form doesn't keep a stale manual entry.
                if (val)
                  register("amenities").onChange(
                    unit.pms_values?.amenity_ids ?? []
                  );
              }}
              name="amenities"
            />
          </div>
          <PMSValue
            value={
              unit.pms_values?.amenities?.length
                ? unit.pms_values.amenities.join(", ")
                : undefined
            }
          />
        </div>
      </div>
    </SectionCard>
  );
};

const EditAmenitiesForm = ({
  onClose,
  initialValues,
  unit
}: {
  onClose: () => void;
  initialValues: {
    dogs_allowed?: boolean;
    cats_allowed?: boolean;
    amenities: number[];
  };
  unit: Unit;
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);
  const [petPolicyTouched, setPetPolicyTouched] = useState<{
    cats_allowed: boolean;
    dogs_allowed: boolean;
  }>({ cats_allowed: false, dogs_allowed: false });

  const { mutate: updateAmenities, isPending } = useMutation({
    mutationFn: () => {
      const petPolicy = {
        ...(petPolicyTouched.cats_allowed && {
          cats_allowed: values.cats_allowed
        }),
        ...(petPolicyTouched.dogs_allowed && {
          dogs_allowed: values.dogs_allowed
        })
      };
      return instance.patch(`/portal/v1/units/${unit.id}`, {
        amenity_ids: values.amenities,
        ...(Object.keys(petPolicy).length > 0 && { pet_policy: petPolicy })
      });
    },
    onSuccess: () => {
      toast.success("Amenities updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update amenities: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => {
      updateAmenities();
    }
  });

  return (
    <AmenitiesForm
      isUnit
      level="unit"
      register={register as unknown as Register}
      selectedAmenities={values.amenities}
      onPetPolicyChange={(field) =>
        setPetPolicyTouched((prev) => ({ ...prev, [field]: true }))
      }
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    />
  );
};

interface AmenitiesSectionProps {
  unit: Unit;
}

export const AmenitiesSection = ({ unit }: AmenitiesSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { type } = useAuthContext();
  const { canEditUnitDetails, unitEditDeniedTooltip } =
    useEditDetailsPermissions();
  const isPmsUnit = type.isPmsListingSyncEnabled && !!unit.pms_id;

  if (isEditing) {
    if (isPmsUnit)
      return (
        <PmsEditAmenitiesForm
          onClose={() => setIsEditing(false)}
          initialValues={{
            dogs_allowed: unit.dogs_allowed ?? undefined,
            cats_allowed: unit.cats_allowed ?? undefined,
            // When sync is on, mirror the PMS list (even when empty) so the
            // form doesn't show a stale manual amenity that contradicts
            // PMS Value: N/A.
            amenities: unit.pms_overrides?.amenities
              ? (unit.pms_values?.amenity_ids ?? [])
              : unit.amenities?.map((amenity) => amenity.id) || [],
            pms_overrides: {
              cats_allowed: unit.pms_overrides?.cats_allowed ?? true,
              dogs_allowed: unit.pms_overrides?.dogs_allowed ?? true,
              amenities: unit.pms_overrides?.amenities ?? true
            }
          }}
          unit={unit}
        />
      );

    return (
      <EditAmenitiesForm
        onClose={() => setIsEditing(false)}
        initialValues={{
          dogs_allowed: unit.dogs_allowed ?? undefined,
          cats_allowed: unit.cats_allowed ?? undefined,
          amenities: unit.amenities?.map((amenity) => amenity.id) || []
        }}
        unit={unit}
      />
    );
  }

  return (
    <SectionCard
      title="Amenities"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
          disabled={!canEditUnitDetails}
          tooltip={unitEditDeniedTooltip}
          tooltipPlacement="bottom-end"
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4 w-full">
        <Row
          title="Cats allowed"
          value={getYesNo(Boolean(unit.cats_allowed))}
        />
        <Row title="Dogs allowed" value={getDogsAllowedLabel(unit)} />
        <Row
          title="Amenities"
          value={<AmenitiesList amenities={unit.amenities || []} />}
          valueClassName="w-[500px]"
        />
      </div>
    </SectionCard>
  );
};
